import { For, Show } from "solid-js";
import type { Component } from "solid-js";
import { useShellStore } from "@ui/state/shell";
import { useLocationDetailQuery } from "../service";
import { CollapsibleSection } from "@ui/components/CollapsibleSection";

const labelValue = (label: string, value: string | null | undefined) => (
  <div class="detail-field">
    <span class="detail-field__label">{label}</span>
    <span class="detail-field__value">{value ?? "-"}</span>
  </div>
);

export const LocationDetailPanel: Component = () => {
  const { state } = useShellStore();
  const locationId = () => state.selectedLocationId;
  const query = useLocationDetailQuery(locationId);

  const detail = () => query.data ?? null;
  const summary = () => detail()?.summary ?? null;
  const address = () => detail()?.address ?? null;
  const floors = () => detail()?.floors ?? [];

  const isLoading = () => Boolean(query.isLoading);
  const error = () => (query.error instanceof Error ? query.error : query.error ? new Error(String(query.error)) : null);

  return (
    <div class="chat-profile-panel">
      <Show when={locationId()} fallback={<p>Select a location to view details.</p>}>
        <Show when={!isLoading()} fallback={<p>Loading location details...</p>}>
          <Show
            when={!error()}
            fallback={
              <p class="empty-state">
                Unable to load location details right now.
                <Show when={error()?.message}>{(message) => <span class="error-detail">{message()}</span>}</Show>
              </p>
            }
          >
            <Show when={summary()} fallback={<p>Location not found.</p>}>
              <CollapsibleSection title="LOCATION">
                {labelValue("Name", summary()?.name ?? null)}
                {labelValue("Status", summary()?.serviceStatus ?? null)}
                {labelValue("Floors", summary()?.totalFloors != null ? String(summary()?.totalFloors) : null)}
                {labelValue("Apartments", summary()?.totalApartments != null ? String(summary()?.totalApartments) : null)}
              </CollapsibleSection>

              <CollapsibleSection title="ADDRESS">
                <Show when={address()} fallback={<p class="empty-state">Address details unavailable.</p>}>
                  {(loc) => (
                    <div>
                      {labelValue("Street", loc().street)}
                      {labelValue("City", loc().city)}
                      {labelValue("State", loc().state)}
                      {labelValue("Postal code", loc().postalCode)}
                    </div>
                  )}
                </Show>
              </CollapsibleSection>

              <CollapsibleSection title="FLOORS" defaultOpen={false}>
                <Show when={floors().length > 0} fallback={<p class="empty-state">No floor plans uploaded yet.</p>}>
                  <ul class="profile-booking-list">
                    <For each={floors()}>
                      {(floor) => (
                        <li class="profile-booking profile-booking--condensed">
                          <div class="profile-booking__line">
                            <span>{floor.name ?? `Floor ${floor.number ?? ""}`}</span>
                            <span class="profile-booking__amount">{floor.apartments.length} units</span>
                          </div>
                          <Show when={floor.apartments.length > 0}>
                            <div class="profile-booking__line profile-booking__line--muted">
                              {floor.apartments
                                .map((apartment) =>
                                  apartment.unit ? (apartment.squareFeet ? `${apartment.unit} (${apartment.squareFeet} sq ft)` : apartment.unit) : null,
                                )
                                .filter(Boolean)
                                .join(", ") || "Units pending"}
                            </div>
                          </Show>
                        </li>
                      )}
                    </For>
                  </ul>
                </Show>
              </CollapsibleSection>

              <CollapsibleSection title="NOTES" defaultOpen={false}>
                <Show when={detail()?.notes} fallback={<p class="empty-state">No notes for this location yet.</p>}>
                  {(note) => <p>{note()}</p>}
                </Show>
              </CollapsibleSection>
            </Show>
          </Show>
        </Show>
      </Show>
    </div>
  );
};
